/**
 * R15 New ticket — category grid, title, description, photo placeholder
 * (not wired — no upload endpoint yet), location + urgency toggles.
 * Wired: POST /api/helpdesk. Port of design/source/screens-helpdesk.jsx
 * NewTicketScreen.
 */
import { router } from 'expo-router';
import React from 'react';
import { Pressable, View } from 'react-native';

import { radius, space } from '@bero/tokens';
import { ALL_CATEGORIES, backOr, BottomBar, categoryIcon, CATEGORY_LABEL_KEY } from '@/components/money/bits';
import { PhotoAttach } from '@/components/photo/PhotoAttach';
import { createTicket, errorMessage, type TicketCategory } from '@/components/money/api';
import { Icon } from '@/components/money/icons';
import { useT } from '@/i18n';
import { useTheme } from '@/theme';
import { AppText, Button, Field, IconButton, Input, Screen, TopBar } from '@/ui';

type Location = 'PERSONAL' | 'COMMON_AREA';
type Priority = 'NORMAL' | 'URGENT';

export default function NewTicketScreen() {
  const { colors } = useTheme();
  const { t } = useT();
  const [category, setCategory] = React.useState<TicketCategory>('PLUMBING');
  const [title, setTitle] = React.useState('');
  const [description, setDescription] = React.useState('');
  const [location, setLocation] = React.useState<Location>('PERSONAL');
  const [priority, setPriority] = React.useState<Priority>('NORMAL');
  const [photo, setPhoto] = React.useState<string | null>(null);
  const [submitting, setSubmitting] = React.useState(false);
  const [submitError, setSubmitError] = React.useState<string | null>(null);

  const submit = async () => {
    if (!title.trim() || submitting) return;
    setSubmitting(true);
    setSubmitError(null);
    try {
      const ticket = await createTicket({
        title: title.trim().slice(0, 100), category, description: description.trim(), location, priority,
        photos: photo,
      });
      router.replace(`/helpdesk/${ticket.id}`);
    } catch (e) {
      setSubmitError(errorMessage(e, t('hd_raise_error')));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Screen scroll={false}>
      <TopBar
        title={t('hd_new_ticket')}
        onBack={backOr('/(tabs)/home')}
        right={
          <IconButton label={t('close')} onPress={backOr('/(tabs)/home')}>
            <Icon name="x" size={20} color={colors.textPrimary} />
          </IconButton>
        }
      />
      <Screen padded style={{ paddingTop: space.s4 }}>
        <View style={{ gap: space.s4 }}>
          <Field label={t('hd_category')}>
            <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: space.s2 }}>
              {ALL_CATEGORIES.map((cat) => {
                const on = category === cat;
                return (
                  <Pressable
                    key={cat}
                    accessibilityRole="button"
                    accessibilityState={{ selected: on }}
                    onPress={() => setCategory(cat)}
                    style={{
                      width: '31%', minHeight: 64, borderRadius: radius.lg, paddingVertical: 12,
                      alignItems: 'center', justifyContent: 'center', gap: 6,
                      backgroundColor: on ? colors.primary500 : colors.canvas1,
                    }}
                  >
                    <Icon name={categoryIcon(cat)} size={20} color={on ? colors.textOnDark : colors.textPrimary} />
                    <AppText variant="bodySm" weight="500" style={{ color: on ? colors.textOnDark : colors.textPrimary }}>
                      {t(CATEGORY_LABEL_KEY[cat])}
                    </AppText>
                  </Pressable>
                );
              })}
            </View>
          </Field>

          <Field label={t('hd_field_title')} help={t('hd_title_help')}>
            <Input value={title} onChangeText={setTitle} placeholder={t('hd_title_ph')} maxLength={100} />
          </Field>

          <Field label={t('hd_description')}>
            <Input
              value={description}
              onChangeText={setDescription}
              placeholder={t('hd_desc_ph')}
              multiline
              style={{ minHeight: 88, textAlignVertical: 'top', paddingTop: 10 }}
            />
          </Field>

          <Field label={t('hd_photos')}>
            <PhotoAttach value={photo} onChange={setPhoto} />
          </Field>

          <Field label={t('hd_location')}>
            <SegRow
              options={[['PERSONAL', t('hd_loc_personal')], ['COMMON_AREA', t('hd_loc_common')]]}
              value={location}
              onChange={setLocation}
            />
          </Field>

          <Field label={t('hd_priority')}>
            <SegRow options={[['NORMAL', t('hd_prio_normal')], ['URGENT', t('hd_prio_urgent')]]} value={priority} onChange={setPriority} />
          </Field>

          {submitError ? <AppText variant="bodySm" tone="danger">{submitError}</AppText> : null}
        </View>
      </Screen>
      <BottomBar>
        <Button title={t('hd_raise')} size="lg" disabled={!title.trim()} loading={submitting} onPress={() => void submit()} />
      </BottomBar>
    </Screen>
  );
}

function SegRow<T extends string>({ options, value, onChange }: {
  options: ReadonlyArray<readonly [T, string]>; value: T; onChange: (v: T) => void;
}) {
  const { colors } = useTheme();
  return (
    <View style={{ flexDirection: 'row', gap: 6 }}>
      {options.map(([key, label]) => {
        const active = value === key;
        return (
          <Pressable
            key={key}
            accessibilityRole="button"
            accessibilityState={{ selected: active }}
            onPress={() => onChange(key)}
            style={{
              flex: 1, minHeight: 44, paddingHorizontal: 12, borderRadius: radius.md,
              borderWidth: 1, alignItems: 'center', justifyContent: 'center',
              borderColor: active ? colors.borderFirm : colors.border,
              backgroundColor: active ? colors.canvas2 : 'transparent',
            }}
          >
            <AppText variant="bodySm" weight="500" style={{ color: active ? colors.textPrimary : colors.textSecondary }}>{label}</AppText>
          </Pressable>
        );
      })}
    </View>
  );
}
